import React, { useState, useEffect } from 'react'
import {
  FormHelperText,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Chip,
  Avatar,
} from '@mui/material'
import { useInput, useTranslate } from 'react-admin'
import get from 'lodash/get';

export const AutocompleteInput = ({
  source,
  options = [],
  defaultValue,
  validate,
  onChange,
  optionsText = ['long_name'],
  optionValue = 'id',
  separator = ' - ',
  helperText,
  multiple = false,
  onSearch,
  loading,
  resetOnLoad = true,
  disableClearable,
  startAdornment,
  positionOptionsText = null, //position to use in OptionsText
  useOptionValue = false,
  showAllOptionText = false,
  useOptionValueByOptions = false,
  showLabel = false,
  label = '',
  ...props
}) => {
  const emptyValue = multiple ? [] : null
  const translate = useTranslate()
  const [previousOptions, setPreviousOptions] = useState([])
  const [initialData, setInitialData] = useState()
  const { field, fieldState, isRequired } = useInput({
    source,
    validate,
    defaultValue: defaultValue || emptyValue,
  })
  const { error } = fieldState

  useEffect(() => {
    if (defaultValue && !initialData && !loading) {
      onSearch && onSearch(getText(defaultValue, positionOptionsText))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  useEffect(() => {
    if (options && options.length > 0 && defaultValue && !initialData) {
      setInitialData(defaultValue)
      field.onChange(defaultValue)
    }

    if (JSON.stringify(previousOptions) !== JSON.stringify(options) && resetOnLoad && !defaultValue) {
      if (multiple) {
        const value = field.value || []
        field.onChange(
          value?.filter((elem) => options.find((option) => option[optionValue] === elem[optionValue]))
        )
        onChange && onChange({ target: { value: value } })
      } else if (!options.find((elem) => elem[optionValue] === field.value[optionValue])) {
        field.onChange('')
        onChange && onChange({ target: { value: '' } })
      }
    }
    setPreviousOptions(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  const getText = (item, positionOptionsText = null, useOptionValue = false, showAllOptionText = false) => {
    if (item) {
      if (positionOptionsText !== null && !showAllOptionText) {
        if (useOptionValue) {
          const selected = options.find((option) => option?.[optionValue] === item)
          if (selected) {
            return selected[optionsText?.[positionOptionsText]]
          }
        }
        // take positionOptionsText, ex : if is 0, then ["long_name", "fantasy_name"] just use "long_name"
        const value = item?.[optionsText?.[positionOptionsText]]
        if (value !== null && value !== undefined){
          return value
        }
      }

      if (useOptionValue) {
        const selected = options.find((option) => option?.[optionValue] === item)
        if (selected) {
          return optionsText ? optionsText.map((option) => get(selected, option)).join(separator) : selected?.name
        }
      }

      return optionsText ? optionsText.map((option) => get(item, option)).join(separator) : item?.name
    }
    return ''
  }

  const onChangeField = (value) => {
    if (useOptionValue) {
      const data = value[optionValue]
      field.onChange(data)
      onChange && onChange({ target: { data } })
    } else {
      field.onChange(value)
      onChange && onChange({ target: { value } })
    }
  }

  return (
    <>
      <Autocomplete
        {...props}
        value={field.value || null}
        multiple={multiple}
        size='small'
        options={options}
        onChange={(e, value) => onChangeField(value)}
        getOptionLabel={(e) => getText(e, positionOptionsText, useOptionValue, showAllOptionText)}
        isOptionEqualToValue={(option, value) => {
          let compareOption = null
          let compareValue = null
          if (useOptionValueByOptions){
            compareOption = option?.[optionValue]
            compareValue = value
          }else {
            compareOption = getText(option)
            compareValue = getText(value)
          }
          return compareOption === compareValue
        }}
        disableClearable={isRequired || disableClearable}
        noOptionsText={translate('ra.validation.noOptionsText')}
        loading={loading}
        loadingText={translate('ra.action.loading')}
        renderTags={
          props.renderTags
            ? (value, getTagProps) => {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    variant='outlined'
                    label={getText(option)}
                    avatar={option?.symbol && <Avatar>{option?.symbol}</Avatar>}
                    style={{ backgroundColor: option?.color }}
                    size='small'
                  />
                ))
              }
            : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(error)}
            variant='outlined'
            label={showLabel ? label : ''}
            placeholder={translate('ra.input.placeholder.search')}
            onChange={(e) => onSearch && onSearch(e.target.value)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment && <InputAdornment position='start'>{startAdornment}</InputAdornment>}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <FormHelperText className='form-helper-text' error={error?.message ? true : false}>
        {error?.message || helperText}
      </FormHelperText>
    </>
  )
}
